import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTRPC } from "../lib/trpc.js";
import { Card, ErrorBox } from "../components/ui.js";
import { useSession } from "../lib/session.js";
import { invalidateEverything } from "../lib/invalidate.js";

export default function Login() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, isLoading } = useSession();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const from = (location.state as { from?: string } | null)?.from ?? "/submit";

  const login = useMutation(
    trpc.auth.login.mutationOptions({
      onSuccess: async (data) => {
        // Signing in changes who every query is asked as, so this one really is
        // everything.
        await invalidateEverything(queryClient);
        navigate(data.mustChangePassword ? "/account" : from, { replace: true });
      },
    }),
  );

  if (!isLoading && user) return <Navigate to={from} replace />;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate({ username: username.trim(), password });
  };

  return (
    <div className="mx-auto max-w-sm py-8">
      <h1 className="mb-2 text-2xl font-bold">Sign in</h1>
      <p className="mb-6 text-sm text-chalk-400">
        Managers sign in to file their half of a result.
      </p>

      <Card>
        <form onSubmit={submit} className="space-y-4 p-5">
          <div>
            <label className="label" htmlFor="username">
              Username
            </label>
            <input
              id="username"
              className="input"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
            />
          </div>

          <div>
            <label className="label" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              className="input"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {login.error && <ErrorBox error={login.error} />}

          <button
            type="submit"
            className="btn-primary w-full"
            disabled={login.isPending}
          >
            {login.isPending ? "Signing in…" : "Sign in"}
          </button>

          <p className="text-xs text-chalk-500">
            Don't have a login? Ask the Commissioner to set one up.
          </p>
        </form>
      </Card>
    </div>
  );
}
